import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, eachDayOfInterval, isSameDay, isSameMonth } from 'date-fns';
import { cn } from '../lib/utils';

export default function CalendarPage() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('month');
    const [selectedPost, setSelectedPost] = useState(null);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const start = startOfMonth(subMonths(currentDate, 1));
            const end = endOfMonth(addMonths(currentDate, 1));
            const response = await api.get(`/calendar/posts?start=${start.toISOString()}&end=${end.toISOString()}`);
            setPosts(response.data);
        } catch (error) {
            console.error('Failed to fetch posts:', error);
        } finally {
            setLoading(false);
        }
    }, [api, currentDate]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const getPostsForDay = (date) => {
        return posts.filter(post => {
            const postDate = new Date(post.start);
            return isSameDay(postDate, date);
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: 'bg-muted text-muted-foreground',
            scheduled: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/50',
            published: 'bg-green-500/20 text-green-500 border-green-500/50',
            failed: 'bg-destructive/20 text-destructive border-destructive/50',
            pending_approval: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50'
        };
        return colors[status] || colors.draft;
    };

    const getPlatformEmoji = (platform) => {
        const emojis = {
            instagram: '📸',
            facebook: '👤',
            twitter: '🐦',
            linkedin: '💼',
            tiktok: '🎵',
            pinterest: '📌',
            youtube: '🎬'
        };
        return emojis[platform] || '📱';
    };

    const renderMonthView = () => {
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        const calendarStart = startOfWeek(monthStart);
        const calendarEnd = endOfWeek(monthEnd);
        const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

        return (
            <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                        {day}
                    </div>
                ))}
                {days.map(day => {
                    const dayPosts = getPostsForDay(day);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, new Date());

                    return (
                        <div
                            key={day.toISOString()}
                            className={cn(
                                "min-h-[120px] p-2 border border-border rounded-lg transition-colors",
                                !isCurrentMonth && "opacity-40",
                                isToday && "border-primary bg-primary/5"
                            )}
                        >
                            <div className={cn(
                                "text-sm font-medium mb-1",
                                isToday && "text-primary"
                            )}>
                                {format(day, 'd')}
                            </div>
                            <div className="space-y-1">
                                {dayPosts.slice(0, 3).map(post => (
                                    <div
                                        key={post.id}
                                        className={cn(
                                            "calendar-post-card text-xs p-1.5 rounded border cursor-pointer",
                                            getStatusColor(post.status)
                                        )}
                                        onClick={() => setSelectedPost(post)}
                                        data-testid={`calendar-post-${post.id}`}
                                    >
                                        <div className="flex items-center gap-1 mb-0.5">
                                            {post.platforms?.slice(0, 2).map(p => (
                                                <span key={p}>{getPlatformEmoji(p)}</span>
                                            ))}
                                        </div>
                                        <div className="truncate">{post.title}</div>
                                    </div>
                                ))}
                                {dayPosts.length > 3 && (
                                    <div className="text-xs text-muted-foreground text-center">
                                        +{dayPosts.length - 3} more
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderWeekView = () => {
        const weekStart = startOfWeek(currentDate);
        const weekEnd = endOfWeek(currentDate);
        const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

        return (
            <div className="grid grid-cols-7 gap-2">
                {days.map(day => {
                    const dayPosts = getPostsForDay(day);
                    const isToday = isSameDay(day, new Date());

                    return (
                        <div key={day.toISOString()} className="space-y-2">
                            <div className={cn(
                                "text-center p-2 rounded-lg",
                                isToday && "bg-primary text-primary-foreground"
                            )}>
                                <div className="text-xs text-muted-foreground">
                                    {format(day, 'EEE')}
                                </div>
                                <div className="text-lg font-bold">
                                    {format(day, 'd')}
                                </div>
                            </div>
                            <div className="space-y-2 min-h-[300px]">
                                {dayPosts.map(post => (
                                    <div
                                        key={post.id}
                                        className={cn(
                                            "p-2 rounded-lg border cursor-pointer",
                                            getStatusColor(post.status)
                                        )}
                                        onClick={() => setSelectedPost(post)}
                                    >
                                        <div className="text-xs text-muted-foreground mb-1">
                                            {format(new Date(post.start), 'h:mm a')}
                                        </div>
                                        <div className="flex gap-1 mb-1">
                                            {post.platforms?.map(p => (
                                                <span key={p} className="text-xs">{getPlatformEmoji(p)}</span>
                                            ))}
                                        </div>
                                        <div className="text-sm truncate">{post.title}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Content Calendar</h1>
                    <p className="text-muted-foreground mt-1">
                        Schedule and manage your posts
                    </p>
                </div>
                <Button 
                    className="gradient-bg"
                    onClick={() => navigate('/compose')}
                    data-testid="calendar-create-post-btn"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Post
                </Button>
            </div>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setCurrentDate(prev => view === 'month' ? subMonths(prev, 1) : new Date(prev.setDate(prev.getDate() - 7)))}
                            data-testid="calendar-prev-btn"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <CardTitle className="text-xl">
                            {format(currentDate, view === 'month' ? 'MMMM yyyy' : "'Week of' MMM d, yyyy")}
                        </CardTitle>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setCurrentDate(prev => view === 'month' ? addMonths(prev, 1) : new Date(prev.setDate(prev.getDate() + 7)))}
                            data-testid="calendar-next-btn"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                    <Tabs value={view} onValueChange={setView}>
                        <TabsList>
                            <TabsTrigger value="month" data-testid="view-month-btn">Month</TabsTrigger>
                            <TabsTrigger value="week" data-testid="view-week-btn">Week</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="h-[600px] flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        </div>
                    ) : view === 'month' ? renderMonthView() : renderWeekView()}
                </CardContent>
            </Card>

            <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Post Details</DialogTitle>
                    </DialogHeader>
                    {selectedPost && (
                        <div className="space-y-4">
                            <div>
                                <div className="text-sm text-muted-foreground">Caption</div>
                                <div className="mt-1">{selectedPost.title}</div>
                            </div>
                            <div>
                                <div className="text-sm text-muted-foreground">Scheduled</div>
                                <div className="mt-1">
                                    {format(new Date(selectedPost.start), 'PPP p')}
                                </div>
                            </div>
                            <div>
                                <div className="text-sm text-muted-foreground">Platforms</div>
                                <div className="flex gap-2 mt-1">
                                    {selectedPost.platforms?.map(p => (
                                        <Badge key={p} variant="secondary">
                                            {getPlatformEmoji(p)} {p}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="text-sm text-muted-foreground">Status</div>
                                <Badge className={cn("mt-1", getStatusColor(selectedPost.status))}>
                                    {selectedPost.status}
                                </Badge>
                            </div>
                            <div className="flex gap-2 pt-4">
                                <Button
                                    className="flex-1"
                                    onClick={() => {
                                        navigate(`/compose?edit=${selectedPost.id}`);
                                        setSelectedPost(null);
                                    }}
                                >
                                    Edit Post
                                </Button>
                                <Button variant="outline" onClick={() => setSelectedPost(null)}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
